import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
    Truck,
    ShieldCheck,
    RefreshCw,
    ShoppingCart,
    Zap,
    BookOpen,
    CreditCard,
    Clock,
} from "lucide-react";

import Gallery from "@/components/site/Gallery";
import ShareButtons from "@/components/site/ShareButtons";
import BookCard from "@/components/site/BookCard";
import EbookPaymentModal from "@/components/site/EbookPaymentModal";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";

// Main BookDetails Component
export default function BookDetails () {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const { add, items } = useCart();
    const { t, lang } = useI18n();
    const [book, setBook] = useState(null);
    const [related, setRelated] = useState([]);
    const [qty, setQty] = useState(1);
    const [showSample, setShowSample] = useState(false);
    const [showPayment, setShowPayment] = useState(false);
    const [ebookStatus, setEbookStatus] = useState(null);
    const [ebookReadUrl, setEbookReadUrl] = useState(null);

    useEffect(() => {
        api.get(`/books/${ id }`)
            .then((r) => setBook(r.data))
            .catch(() => nav("/"));

        api.get(`/books/${ id }/related`)
            .then((r) => setRelated(r.data || []))
            .catch(() => { });

        window.scrollTo(0, 0);
    }, [id, nav]);

    // Ebook subscription status fetch
    useEffect(() => {
        if (!book || book.category !== "ebook" || !user) return;
        api.get("/profile/ebook-subscriptions")
            .then((r) => {
                const subs = r.data || [];
                const sub = subs.find((s) => s.book_id === book.id);
                if (sub) setEbookStatus(sub.status);
            })
            .catch(() => { });
    }, [book, user]);

    // Ebook active read URL fetch
    const handleReadEbook = async () => {
        try {
            const r = await api.get(`/ebook/read/${ book.id }`);
            setEbookReadUrl(r.data.pdf_url);
        } catch (err) {
            const msg = err?.response?.data?.detail || "ই-বুক লোড করা যায়নি";
            toast.error(msg);
        }
    };

    if (!book) {
        return <div className="text-center py-20">Loading...</div>;
    }

    const isEbook = book.category === "ebook";
    const price = book.discount_price ?? book.price;

    const hasDiscount = book.discount_price && book.discount_price < book.price;

    const samplePdfFull = book.sample_pdf_url
        ? book.sample_pdf_url.startsWith("/")
            ? `${ BACKEND_URL }${ book.sample_pdf_url }`
            : book.sample_pdf_url
        : "";

    const coverImage = book.cover_image?.startsWith("/")
        ? `${ BACKEND_URL }${ book.cover_image }`
        : book.cover_image;

    const alreadyInCart = items?.some((item) => item.book_id === book.id);

    const plainDescription = (book.description || "Best books and eBooks platform in Bangladesh")
        .replace(/<[^>]+>/g, "")
        .slice(0, 160);

    const shareUrl = `${ window.location.origin }/book/${ book.id }`;

    const stock = book.stock ?? 0;
    const isOutOfStock = stock <= 0;

    return (
        <>
            <Helmet>
                <title>{`${ book.title } | Self Publication BD`}</title>
                <meta property="og:title" content={book.title} />
                <meta property="og:description" content={plainDescription} />
                <meta property="og:image" content={coverImage} />
                <meta property="og:url" content={shareUrl} />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={book.title} />
                <meta name="twitter:description" content={plainDescription} />
                <meta name="twitter:image" content={coverImage} />
            </Helmet>

            <div className="bg-gradient-to-b from-white to-brand-light/20">
                {/* PRODUCT SECTION */}
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12 grid md:grid-cols-2 gap-6 sm:gap-10">
                    <Gallery cover={book.cover_image} images={book.gallery_images || []} />

                    <div className="min-w-0">
                        <div className="text-xs font-semibold text-brand-red uppercase">
                            {book.category}
                            {book.subject ? ` • ${ book.subject }` : ""}
                            {isEbook && (
                                <span className="ml-2 bg-brand-royal/10 text-brand-royal px-2 py-0.5 rounded-full text-xs">
                                    📱 ই-বুক
                                </span>
                            )}
                        </div>

                        <h1 className="text-3xl md:text-4xl font-bold text-brand-navy mt-2">
                            {lang === "en" && book.title_en ? book.title_en : book.title}
                        </h1>

                        <div className="text-brand-navy/65 mt-2">
                            By{" "}
                            <span className="font-semibold">
                                {book.author || "Self Preparation"}
                            </span>
                        </div>

                        <div className="mt-2 text-sm text-brand-navy/70">
                            Stock: {stock > 0 ? `${ stock } available` : "Out of stock"}
                        </div>

                        {/* PRICE */}
                        <div className="mt-4 sm:mt-6 flex items-end gap-3">
                            <span className="text-3xl sm:text-4xl font-bold text-brand-royal">৳{price}</span>
                            {hasDiscount && (
                                <>
                                    <span className="text-lg text-brand-navy/40 line-through">
                                        ৳{book.price}
                                    </span>
                                    <span className="bg-brand-red/10 text-brand-red text-xs font-bold px-2 py-1 rounded-md">
                                        {Math.round((1 - book.discount_price / book.price) * 100)}%
                                        OFF
                                    </span>
                                </>
                            )}
                        </div>

                        {/* ═══ ACTIONS ═══ */}
                        <div className="mt-6 space-y-3">
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                                {isEbook ? (
                                    /* ── Ebook actions ── */
                                    ebookStatus === "active" ? (
                                        <button
                                            onClick={() => nav("/profile")}
                                            className="btn-primary px-4 sm:px-6 py-2.5 sm:py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm sm:text-base"
                                        >
                                            <BookOpen className="w-4 h-4 shrink-0" />
                                            বই পড়ুন
                                        </button>
                                    ) : ebookStatus === "pending" ? (
                                        <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-300 rounded-xl px-4 py-2.5 text-yellow-700 text-sm font-semibold">
                                            <Clock className="w-4 h-4 shrink-0" />
                                            যাচাই হচ্ছে...
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                if (!user) { nav("/login"); return; }
                                                setShowPayment(true);
                                            }}
                                            disabled={isOutOfStock}
                                            className={`btn-primary px-4 sm:px-6 py-2.5 sm:py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm sm:text-base ${ isOutOfStock ? "opacity-50 cursor-not-allowed" : "" }`}
                                        >
                                            <CreditCard className="w-4 h-4 shrink-0" />
                                            {isOutOfStock
                                                ? "Out of Stock"
                                                : ebookStatus === "expired"
                                                    ? "পুনরায় সাবস্ক্রাইব করুন"
                                                    : "সাবস্ক্রাইব করুন"}
                                        </button>
                                    )
                                ) : (
                                    /* ── Physical book actions ── */
                                    alreadyInCart ? (
                                        <button
                                            onClick={() => nav("/cart")}
                                            className="btn-primary px-4 sm:px-6 py-2.5 sm:py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm sm:text-base"
                                        >
                                            <ShoppingCart className="w-4 h-4 shrink-0" />
                                            কার্ট দেখুন
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => add(book, qty)}
                                            disabled={isOutOfStock}
                                            className={`btn-primary px-4 sm:px-6 py-2.5 sm:py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm sm:text-base ${ isOutOfStock ? "opacity-50 cursor-not-allowed" : "" }`}
                                        >
                                            <ShoppingCart className="w-4 h-4 shrink-0" />
                                            {isOutOfStock ? "Out of Stock" : t("add_to_cart")}
                                        </button>
                                    )
                                )}

                                {(book.sample_pdf_url || book.sample_pdf_text) && (
                                    <button
                                        onClick={() => setShowSample(true)}
                                        className="btn-accent px-4 sm:px-6 py-2.5 sm:py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm sm:text-base"
                                        data-testid="read-sample-btn"
                                    >
                                        <BookOpen className="w-4 h-4 shrink-0" /> {t("read_sample")} →
                                    </button>
                                )}

                                <ShareButtons compact title={book.title} url={shareUrl} />
                            </div>
                        </div>

                        {/* ই-বুক subscription info note */}
                        {isEbook && !ebookStatus && (
                            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
                                <p className="font-semibold mb-1">
                                    📖 ই-বুক সাবস্ক্রিপশন কীভাবে কাজ করে?
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-xs text-blue-700">
                                    <li>আপনার পছন্দের প্ল্যান বেছে নিন</li>
                                    <li>bKash/Nagad এ পেমেন্ট করুন</li>
                                    <li>Transaction ID দিয়ে সাবমিট করুন</li>
                                    <li>অ্যাডমিন ভেরিফাই করলে ই-বুক পড়তে পারবেন</li>
                                </ul>
                            </div>
                        )}

                        {/* FEATURES */}
                        {!isEbook && (
                            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 text-xs">
                                <div className="p-2 sm:p-3 border rounded-xl text-center">
                                    <Truck className="mx-auto mb-1 w-5 h-5 sm:w-4 sm:h-4" />
                                    ফাস্ট ডেলিভারি
                                </div>
                                <div className="p-2 sm:p-3 border rounded-xl text-center">
                                    <ShieldCheck className="mx-auto mb-1 w-5 h-5 sm:w-4 sm:h-4" />
                                    ক্যাশ অন ডেলিভারি
                                </div>
                                <div className="p-2 sm:p-3 border rounded-xl text-center">
                                    <RefreshCw className="mx-auto mb-1 w-5 h-5 sm:w-4 sm:h-4" />
                                    হোয়াইট প্রিন্ট
                                </div>
                            </div>
                        )}

                        {/* DESCRIPTION TABS */}
                        <div className="mt-6 sm:mt-10">
                            <Tabs defaultValue="description">
                                <TabsList className="w-full sm:w-auto">
                                    <TabsTrigger value="description" className="flex-1 sm:flex-none">
                                        {t("description")}
                                    </TabsTrigger>
                                    <TabsTrigger value="spec" className="flex-1 sm:flex-none">{t("specification")}</TabsTrigger>
                                </TabsList>
                                <TabsContent value="description" className="mt-4">
                                    <div className="prose max-w-none prose-sm sm:prose-base break-words"
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                book.description || "<p>বিবরণ পাওয়া যায়নি।</p>"
                                        }}
                                    />
                                </TabsContent>
                                <TabsContent value="spec" className="mt-4">
                                    <div className="prose max-w-none prose-sm sm:prose-base break-words"
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                book.specification || "<p>স্পেসিফিকেশন পাওয়া যায়নি।</p>"
                                        }}
                                    />
                                </TabsContent>
                            </Tabs>
                        </div>
                    </div>
                </section>

                {/* RELATED BOOKS */}
                {related.length > 0 && (
                    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16">
                        <h2 className="text-xl sm:text-2xl font-bold text-brand-navy mb-4 sm:mb-6">সম্পর্কিত বই</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                            {related.map((b) => (
                                <BookCard key={b.id} book={b} />
                            ))}
                        </div>
                    </section>
                )}

                {/* SAMPLE DIALOG */}
                <Dialog open={showSample} onOpenChange={setShowSample}>
                    <DialogContent className="max-w-3xl font-bn" data-testid="sample-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-brand-navy text-2xl">
                                📖 {t("read_sample")} • {book.title}
                            </DialogTitle>
                        </DialogHeader>
                        {samplePdfFull ? (
                            <div className="rounded-xl overflow-hidden border border-brand-light bg-white h-[65vh]">
                                <iframe
                                    src={samplePdfFull}
                                    title="PDF Preview"
                                    className="w-full h-full"
                                />
                            </div>
                        ) : (
                            <div className="bg-gradient-to-br from-brand-light/40 to-white rounded-xl p-6 max-h-[65vh] overflow-y-auto whitespace-pre-line text-brand-navy/85 leading-relaxed font-bn">
                                {book.sample_pdf_text || "এই বইটির স্যাম্পল পাওয়া যায়নি।"}
                            </div>
                        )}

                    </DialogContent>
                </Dialog>

                {/* Active subscription */}
                <Dialog open={!!ebookReadUrl} onOpenChange={() => setEbookReadUrl(null)}>
                    <DialogContent className="max-w-4xl font-bn">
                        <DialogHeader>
                            <DialogTitle className="text-brand-navy text-xl">
                                📖 {book.title}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="rounded-xl overflow-hidden border border-brand-light bg-white h-[75vh]">
                            <iframe src={ebookReadUrl} title="E-Book" className="w-full h-full" />
                        </div>
                    </DialogContent>
                </Dialog>

                {/* PAYMENT MODAL */}
                <EbookPaymentModal
                    book={book}
                    open={showPayment}
                    onClose={() => setShowPayment(false)}
                />

            </div>
        </>
    );
}